import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { purchaseRequestsApi } from '../lib/api';
import { PR_STATUS_LABELS, PR_STATUS_COLORS, PR_TYPE_LABELS, ROLE_LABELS } from '../types';
import type { PurchaseRequest } from '../types';
import AppShell from '../components/AppShell';

const SANS = "'Roboto', system-ui, sans-serif";
const MONO = "'Courier New', monospace";

export default function PurchaseRequestsPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [prs, setPrs] = useState<PurchaseRequest[]>([]);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    purchaseRequestsApi
      .list()
      .then(setPrs)
      .catch((e) => setError(e.message))
      .finally(() => setFetching(false));
  }, [user]);

  if (loading || !user) return null;

  const scopeLabel =
    user.role === 'cfo' || user.role === 'procurement_head' || user.role === 'super_admin'
      ? 'All requests'
      : user.role === 'procurement_manager'
      ? 'Your department'
      : 'Your requests';

  const subtitle = `${ROLE_LABELS[user.role]} · ${scopeLabel}${prs.length > 0 ? ` · ${prs.length} total` : ''}`;

  const actions = (
    <Link to="/purchase-requests/new" style={{ textDecoration: 'none' }}>
      <button style={{
        padding: '7px 14px', borderRadius: 8, fontSize: 12.5, fontWeight: 400,
        cursor: 'pointer', border: 'none', fontFamily: SANS,
        background: '#0ea5e9', color: '#ffffff', transition: 'all 0.15s',
        boxShadow: '0 1px 3px rgba(14,165,233,0.3)',
      }} className="sl-btn-primary">
        + New Request
      </button>
    </Link>
  );

  return (
    <AppShell user={user} title="Purchase Requests" subtitle={subtitle} actions={actions}>
      {error && <ErrorBanner message={error} />}

      {fetching ? (
        <p style={{ color: '#94a3b8', fontSize: '0.9rem' }}>Loading…</p>
      ) : prs.length === 0 ? (
        <EmptyState onNew={() => navigate('/purchase-requests/new')} />
      ) : (
        <div style={{
          borderRadius: 10, overflow: 'hidden',
          border: '1px solid #e2e8f0',
          background: '#ffffff',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        }}>
          <div style={headerRow}>
            <span style={{ flex: '0 0 72px' }}>#</span>
            <span style={{ flex: '2' }}>Title / Vendor</span>
            <span style={{ flex: '1', textAlign: 'right' }}>Amount</span>
            <span style={{ flex: '1' }}>Type</span>
            <span style={{ flex: '1.2' }}>Awaiting</span>
            <span style={{ flex: '1' }}>Submitted</span>
            <span style={{ flex: '0 0 130px', textAlign: 'center' }}>Status</span>
          </div>
          {prs.map((pr, i) => (
            <PrRow key={pr.id} pr={pr} index={i} onClick={() => navigate(`/purchase-requests/${pr.id}`)} />
          ))}
        </div>
      )}
    </AppShell>
  );
}

function PrRow({ pr, index, onClick }: { pr: PurchaseRequest; index: number; onClick: () => void }) {
  const amount = parseFloat(pr.amount).toLocaleString('en-US', { style: 'currency', currency: pr.currency });
  const vendorName = pr.vendor?.name ?? (pr.metadata?.vendorName as string) ?? '—';
  const shortId = pr.id.slice(-6).toUpperCase();

  const currentApprover = pr.currentApprovalStep?.approver?.name
    ?? pr.currentApprovalStep?.approverRole
    ?? null;

  return (
    <div
      onClick={onClick}
      style={{
        backgroundColor: '#ffffff',
        borderTop: index > 0 ? '1px solid #e2e8f0' : undefined,
        display: 'flex', alignItems: 'center', gap: '1rem',
        padding: '0.85rem 1.25rem', cursor: 'pointer',
        transition: 'background-color 0.1s',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f8fafc')}
      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#ffffff')}
    >
      <span style={{ flex: '0 0 72px', fontSize: '0.72rem', color: '#94a3b8', fontFamily: MONO }}>
        #{shortId}
      </span>
      <div style={{ flex: '2', minWidth: 0 }}>
        <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 400, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {pr.title}
        </p>
        <p style={{ margin: '0.15rem 0 0', fontSize: '0.72rem', color: '#94a3b8' }}>
          {vendorName}{pr.requester?.name ? ` · ${pr.requester.name}` : ''}
        </p>
      </div>
      <div style={{ flex: '1', textAlign: 'right' }}>
        <span style={{ fontSize: '0.9rem', fontWeight: 400, color: '#0f172a', whiteSpace: 'nowrap', fontFamily: MONO }}>
          {amount}
        </span>
      </div>
      <div style={{ flex: '1' }}>
        <span style={{ fontSize: '0.75rem', color: '#64748b' }}>{PR_TYPE_LABELS[pr.type]}</span>
      </div>
      <div style={{ flex: '1.2' }}>
        {pr.status === 'pending_approval' && currentApprover ? (
          <span style={{ fontSize: '0.75rem', color: '#f59e0b' }}>⏳ {currentApprover}</span>
        ) : pr.status === 'approved' ? (
          <span style={{ fontSize: '0.75rem', color: '#10b981' }}>Approved</span>
        ) : pr.status === 'rejected' ? (
          <span style={{ fontSize: '0.75rem', color: '#ef4444' }}>Rejected</span>
        ) : (
          <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>—</span>
        )}
      </div>
      <div style={{ flex: '1' }}>
        <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
          {pr.submittedAt ? formatDate(pr.submittedAt) : pr.status === 'draft' ? 'Draft' : '—'}
        </span>
      </div>
      <div style={{ flex: '0 0 130px', display: 'flex', justifyContent: 'center' }}>
        <StatusBadge status={pr.status} />
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: PurchaseRequest['status'] }) {
  const color = PR_STATUS_COLORS[status];
  return (
    <span style={{
      fontSize: '0.7rem', fontWeight: 400, color,
      border: `1px solid ${color}`, borderRadius: '20px',
      padding: '0.2rem 0.65rem', whiteSpace: 'nowrap',
    }}>
      {PR_STATUS_LABELS[status]}
    </span>
  );
}

function EmptyState({ onNew }: { onNew: () => void }) {
  return (
    <div style={{
      textAlign: 'center', padding: '4rem 2rem',
      backgroundColor: '#ffffff', border: '1px dashed #cbd5e1', borderRadius: 10,
    }}>
      <p style={{ fontSize: '1rem', color: '#64748b', marginBottom: '1.5rem' }}>No purchase requests yet.</p>
      <button onClick={onNew} style={{
        backgroundColor: '#0ea5e9', color: '#ffffff', border: 'none', borderRadius: 8,
        padding: '0.6rem 1.25rem', fontSize: '0.85rem', fontWeight: 400, cursor: 'pointer',
        fontFamily: SANS,
      }}>
        Raise your first request
      </button>
    </div>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div style={{
      backgroundColor: '#fee2e2', border: '1px solid rgba(239,68,68,0.3)',
      borderRadius: 8, padding: '0.75rem 1rem', color: '#991b1b',
      fontSize: '0.85rem', marginBottom: '1rem',
    }}>
      {message}
    </div>
  );
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

const headerRow: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: '1rem',
  padding: '0.6rem 1.25rem', backgroundColor: '#f8fafc',
  borderBottom: '1px solid #e2e8f0',
  fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#94a3b8',
  fontWeight: 500,
};
